/* eslint-disable react/prop-types */
import React, { useRef, useCallback, useContext } from 'react';
import {
  bool as boolType,
  func,
  string as stringType,
  number as numberType,
} from 'prop-types';
import { useDataModel, extend } from '@thd-nucleus/data-sources';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { Typography } from '@one-thd/sui-atomic-components';
import className from 'classnames';
import { dynamicRecsDataModel } from '../dataModel';
import { isLoading, getAppId, getSkippers } from '../utils/helpers';
import { ProductPodV7Carousel } from '../core/ProductPodV7Carousel';
import { CollectionCarousel } from '../core/CollectionCarousel';
import '../../styles/thd-recs-containers.scss';

let obj = null;
let isTNT = false;
if (typeof LIFE_CYCLE_EVENT_BUS !== 'undefined') {
  LIFE_CYCLE_EVENT_BUS.lifeCycle.on('recs.tnt', (data) => {
    isTNT = data?.eventName === 'tnt';
    obj = data?.output;
  });
}

const DynamicRecs = (props) => {
  const { storeId, storeZip: zipCode, isLocalized, membershipInformation } = useStore();
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const loadedStoreInfo = useRef({ storeId, zipCode });
  const {
    anchorId,
    anchorImage,
    schemaName,
    showLoading,
    serviceType,
    experienceName,
    maxResults,
    requestKey,
    hideSwatches,
    hideATC,
    hideFavorites
  } = props;

  const tntSchema = obj?.originalScheme === schemaName && obj?.replacedScheme;

  const opts = {
    variables: {
      anchorId,
      storeId,
      key: requestKey,
      zipCode: zipCode || '12345',
      apiName: (isTNT && tntSchema) ? tntSchema : schemaName,
      skipInstallServices: true,
      dataSource: schemaName,
      loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership || null,
      serviceType,
      maxResults,
      appId: getAppId(channel, isConsumerApp)
    },
    skip: !isLocalized,
    ssr: false
  };
  const { data, variables, loading, error } = useDataModel('recs', opts);

  if (data && !loading && isLocalized) {
    loadedStoreInfo.current = {
      storeId: variables.storeId,
      zipCode: variables.zipCode,
      mounted: true
    };
  }
  // mounted is when you want it to fetch client() only fields
  // such  as fulfillment. You want to use this flag
  // for inner queries to not change to localized values
  // storeId, zip, until the outer query has resolved
  const mountedFn = useCallback(
    () => loadedStoreInfo.current.mounted,
    [loadedStoreInfo.current.mounted]
  );

  const defaultVariables = {
    storeId: loadedStoreInfo.current.storeId,
    zipCode: loadedStoreInfo.current.zipCode
  };

  const wrapperClasses = className(
    'dynamic-recs',
    'sui-p-4',
    schemaName,
    experienceName,
    {
      loading: showLoading && loading,
    }
  );

  if (error || (data !== undefined && (!data || !data?.recs) && !loading)) {
    if ((schemaName === 'PLP_Search_In_Stock' || schemaName === 'PLP_Browse_In_Stock')) {
      return (
        <Typography variant="body-base" component="h4">
          <div className="sui-flex sui-justify-center sui-items-center">
            No similar items found
          </div>
        </Typography>
      );
    } return null;
  }

  const anchorIdForAnalytics = anchorId?.split(',')[0];

  return (
    <div
      id={schemaName}
      className={wrapperClasses}
      data-type="container"
      data-component={`DynamicRecs-${schemaName}`}
    >
      <meta data-prop="name" content={schemaName} />
      {schemaName !== 'pipcollection'
        ? (
          <ProductPodV7Carousel
            anchorId={anchorId}
            anchorImage={anchorImage}
            itemClass="fixed-item"
            scheme={schemaName}
            loading={isLoading(data, loading)}
            data={data?.recs}
            hideSwatches={hideSwatches}
            hideATC={hideATC}
            hideFavorites={hideFavorites}
            storeId={loadedStoreInfo.current.storeId}
            membershipInformation={membershipInformation}
            zipCode={loadedStoreInfo.current.zipCode}
            parent="recs-product-pod-carousel"
            analyticsAnchorProductSku={anchorIdForAnalytics}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          />
        )
        : (
          <CollectionCarousel
            anchorId={anchorId}
            anchorImage={anchorImage}
            itemClass="fixed-item"
            experienceName={experienceName}
            scheme={schemaName}
            loading={isLoading(data, loading)}
            data={data?.recs}
            hideSwatches={hideSwatches}
            hideATC={hideATC}
            hideFavorites={hideFavorites}
            storeId={loadedStoreInfo.current.storeId}
            membershipInformation={membershipInformation}
            zipCode={loadedStoreInfo.current.zipCode}
            parent="recs-product-pod-carousel"
            analyticsAnchorProductSku={anchorIdForAnalytics}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          />
        )}
    </div>

  );
};

const dataModel = dynamicRecsDataModel;
DynamicRecs.dataModel = dataModel;

DynamicRecs.displayName = 'DynamicRecsComponent';
const propTypes = {
  anchorId: stringType.isRequired,
  anchorImage: stringType,
  collectionUrl: stringType,
  experienceName: stringType,
  hideFavorites: boolType,
  hideFulfillment: boolType,
  maxItems: numberType,
  schemaName: stringType.isRequired,
  serviceType: stringType,
  showLoading: boolType,
  maxResults: stringType,
  requestKey: stringType,
  hideATC: boolType,
  hideLimitPerOrder: boolType,
  hidePriceBadge: boolType,
  hideRebates: boolType,
  hideSavingsText: boolType,
  hideShopThisCollection: boolType,
  hideShippingInfo: boolType,
  loadingImageHeight: numberType,
  loadingLinesCount: numberType
};

DynamicRecs.propTypes = propTypes;

const defaultProps = {
  anchorImage: null,
  collectionUrl: null,
  experienceName: null,
  hideFavorites: true,
  hideFulfillment: true,
  maxItems: 16,
  serviceType: null,
  maxResults: null,
  showLoading: true,
  hideATC: false,
  hideLimitPerOrder: true,
  hidePriceBadge: true,
  hideRebates: true,
  hideSavingsText: true,
  hideShopThisCollection: true,
  hideShippingInfo: true,
  loadingImageHeight: null,
  loadingLinesCount: 5,
  requestKey: 'aGAQFG4j6QtVTSWqujfFYWeIU6BR5Mee'
};

DynamicRecs.defaultProps = defaultProps;

DynamicRecs.propTypes = propTypes;
DynamicRecs.defaultProps = defaultProps;
DynamicRecs.dataModel = dataModel;

export default DynamicRecs;
